import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { mediaUrl, videoThumb } from "@/data/site";
import { api, errMsg } from "@/lib/api";
import { inputCls, btnCls } from "./LoginCard";
import { FileOrLink } from "./FileOrLink";

const CFG = {
  video: { label: "video", accept: "video/*", hint: "YouTube link or upload a video file", cover: true },
  song: { label: "song", accept: "audio/*", hint: "Upload an audio file, or paste a Spotify / Apple / YouTube link", cover: true },
  photo: { label: "photo", accept: "image/*", hint: "Upload a photo or paste an image link", cover: false },
};
const BLANK = { title: "", artist: "", url: "", cover: "" };

const thumb = (type, it) => (type === "photo" ? mediaUrl(it.url) : type === "video" ? videoThumb(it) : it.cover ? mediaUrl(it.cover) : "/mae-logo-ink.webp");

export const ItemsPanel = ({ type, items, onChange }) => {
  const cfg = CFG[type];
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error(`Add a link or upload a ${cfg.label} first`); return; }
    setBusy(true);
    try {
      await api.post("/admin/items", { ...form, type });
      toast.success(`New ${cfg.label} added`);
      setForm(BLANK);
      onChange();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(`Remove this ${cfg.label}?`)) return;
    try { await api.delete(`/admin/items/${id}`); toast.success("Removed"); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-10">
      <form onSubmit={add} data-testid={`admin-${type}-form`} className="space-y-3 rounded-[24px] border border-[var(--line)] bg-[var(--ink-2)] p-5 sm:p-6">
        <p className="font-display text-2xl uppercase">Add {cfg.label}</p>
        <p className="text-sm text-zinc-400">{cfg.hint}</p>
        <FileOrLink testid={`admin-${type}-media`} accept={cfg.accept} placeholder="Link…" value={form.url} onChange={(v) => setForm({ ...form, url: v })} />
        <div className="grid gap-3 sm:grid-cols-2">
          <input data-testid={`admin-${type}-title`} className={inputCls} placeholder={type === "photo" ? "Caption (optional)" : "Title"} value={form.title} onChange={set("title")} />
          {type !== "photo" && <input data-testid={`admin-${type}-artist`} className={inputCls} placeholder="Artist" value={form.artist} onChange={set("artist")} />}
        </div>
        {cfg.cover && <FileOrLink testid={`admin-${type}-cover`} accept="image/*" placeholder="Cover image (optional)" value={form.cover} onChange={(v) => setForm({ ...form, cover: v })} />}
        <button data-testid={`admin-${type}-add`} disabled={busy} className={btnCls}><Plus size={16} /> Add {cfg.label}</button>
      </form>

      <div data-testid={`admin-${type}-list`} className="space-y-3">
        {items.length === 0 && <p className="text-zinc-400">No {cfg.label}s yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 rounded-2xl border border-[var(--line)] p-3">
            <img src={thumb(type, it)} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{it.title || "Untitled"}</p>
              <p className="truncate text-xs text-zinc-400">{it.artist || it.url}</p>
            </div>
            <button data-testid={`admin-delete-${it.id}`} onClick={() => remove(it.id)} aria-label="Delete" className="rounded-full p-3 text-zinc-400 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
